import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, GripVertical, Plus, Trash2 } from "lucide-react";

/**
 * Reorder the slides of a slideshow.
 *
 * Drag-and-drop plus explicit move buttons, not drag alone. The HTML5 drag
 * API is mouse-only — there is no keyboard equivalent — so a grid that can
 * only be dragged is unusable without a pointer. The buttons are the real
 * interface; dragging is the shortcut. (This codebase already had a
 * decorative drag handle on the storyboard that implemented nothing; the
 * point here is that every affordance shown actually works.)
 *
 * `order` is emitted as indices into the CURRENT list, because screenshot URLs
 * are regenerated as presigned links on every read and cannot be used as ids.
 */
export default function ScreenshotReorder({
  screenshots = [],
  onReorder,
  onDelete,
  onAdd,
  disabled = false,
  selectedIdx = null,
  onSelect,
}) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const buttonsRef = useRef({});

  const move = (from, to) => {
    if (disabled || to < 0 || to >= screenshots.length || from === to) return;
    const order = screenshots.map((_, i) => i);
    order.splice(to, 0, order.splice(from, 1)[0]);
    setAnnouncement(`Slide moved from position ${from + 1} to ${to + 1} of ${screenshots.length}.`);
    onReorder(order, to);
    // Keep focus on the control the user just pressed, which has now moved.
    requestAnimationFrame(() => buttonsRef.current[`${to}`]?.focus());
  };

  const onDrop = (to) => {
    if (dragIdx === null) return;
    move(dragIdx, to);
    setDragIdx(null);
    setOverIdx(null);
  };

  if (!screenshots.length) return null;

  return (
    <div data-testid="screenshot-reorder">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="brutal-label !mb-0">Slide order</h3>
        <p className="text-xs text-ink-muted">
          Drag, or use the arrows — this is the order they play in.
        </p>
      </div>

      <ol className="grid grid-cols-3 md:grid-cols-6 gap-2 list-none p-0 m-0">
        {screenshots.map((src, i) => (
          <li
            key={`${src}-${i}`}
            draggable={!disabled}
            onDragStart={() => setDragIdx(i)}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={(e) => { e.preventDefault(); onDrop(i); }}
            className={`relative brutal-border bg-canvas ${
              overIdx === i && dragIdx !== null ? "outline outline-2 outline-offset-2 outline-brand" : ""
            } ${dragIdx === i ? "opacity-50" : ""}`}
            data-testid={`slide-${i}`}
          >
            <button
              type="button"
              onClick={() => onSelect?.(i)}
              className={`block w-full aspect-video overflow-hidden ${
                selectedIdx === i ? "outline outline-2 outline-offset-[-2px] outline-brand" : ""
              }`}
              aria-label={`Slide ${i + 1} of ${screenshots.length}${onSelect ? " — open to annotate" : ""}`}
              aria-pressed={selectedIdx === i}
              disabled={disabled}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>

            {/* Position badge: the number IS the sequence, so it must be
                visible rather than implied by grid order alone. */}
            <span className="absolute top-1 left-1 bg-surface-inverse text-ink-inverse text-2xs px-1.5 py-0.5 font-mono-ui tabular-nums">
              {i + 1}
            </span>
            <GripVertical
              size={12}
              className="absolute top-1 right-1 text-ink-inverse opacity-70 pointer-events-none"
              aria-hidden="true"
            />

            <div className="flex border-t brutal-border border-l-0 border-r-0 border-b-0">
              <button
                type="button"
                onClick={() => move(i, i - 1)}
                disabled={disabled || i === 0}
                className="flex-1 py-1 hover:bg-surface disabled:opacity-30 flex justify-center"
                aria-label={`Move slide ${i + 1} earlier`}
                data-testid={`slide-${i}-earlier`}
              >
                <ChevronLeft size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                ref={(el) => { buttonsRef.current[`${i}`] = el; }}
                onClick={() => move(i, i + 1)}
                disabled={disabled || i === screenshots.length - 1}
                className="flex-1 py-1 border-l brutal-border border-t-0 border-r-0 border-b-0 hover:bg-surface disabled:opacity-30 flex justify-center"
                aria-label={`Move slide ${i + 1} later`}
                data-testid={`slide-${i}-later`}
              >
                <ChevronRight size={14} aria-hidden="true" />
              </button>
              {onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm(`Remove slide ${i + 1} from this demo?`)) return;
                    setAnnouncement(`Slide ${i + 1} removed. ${screenshots.length - 1} remaining.`);
                    onDelete(i);
                  }}
                  disabled={disabled}
                  className="flex-1 py-1 border-l brutal-border border-t-0 border-r-0 border-b-0 hover:bg-state-danger-surface disabled:opacity-30 flex justify-center"
                  aria-label={`Remove slide ${i + 1}`}
                  data-testid={`slide-${i}-delete`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </div>
          </li>
        ))}
        {onAdd && (
          <li className="brutal-border border-dashed">
            <label
              className={`field-focusable flex flex-col items-center justify-center gap-1 w-full h-full min-h-[5rem] cursor-pointer hover:bg-surface ${
                disabled ? "opacity-40 pointer-events-none" : ""
              }`}
              data-testid="slide-add"
            >
              <Plus size={18} aria-hidden="true" />
              <span className="text-2xs uppercase tracking-widest">Add</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                disabled={disabled}
                onChange={(e) => {
                  const files = e.target.files;
                  if (files?.length) onAdd(files);
                  e.target.value = "";   // re-selecting the same file must re-fire
                }}
              />
            </label>
          </li>
        )}
      </ol>

      {/* Drag gives no feedback a screen reader can hear, and neither does a
          grid silently reflowing. */}
      <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
    </div>
  );
}
